import React from 'react';
import { Link } from 'react-router-dom';
import SectionMedia from '../components/SectionMedia';
import { 
  Heart, 
  Eye, 
  Target, 
  Shield, 
  Users, 
  HandHeart,
  ArrowRight,
  CheckCircle,
  ImageIcon
} from 'lucide-react';
import { useSiteContent } from '../hooks/useSiteContent';

const About = () => {
  const howWeImpact = [
    'Work hands-on and directly with communities',
    'Provide honest impact updates and financial summaries',
    'Protect the privacy and dignity of those we serve',
    'Maintain a lean, mission-focused operation to maximize resources',
  ];

  const { data: cms } = useSiteContent('about_page', {
    page_title: 'About Us',
    who_we_are_eyebrow: 'Who We Are',
    who_we_are_heading: 'Novena Humanitarian Foundation',
    who_we_are_paragraphs: [],
    mission_heading: 'Our Mission',
    mission_paragraphs: [],
    vision_heading: 'Our Vision',
    vision_paragraphs: [],
  });

  return (
    <div>
      {/* Red Header Bar */}
      <div className="bg-[#BA1826] py-6 text-center">
        <h1 className="font-heading text-3xl sm:text-4xl font-bold text-white" data-testid="about-page-title">{cms.page_title}</h1>
      </div>

      {/* Who We Are Section */}
      <section className="section-padding bg-white" data-testid="who-we-are">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-sm uppercase tracking-[0.15em] text-[#BA1826] font-semibold mb-4">
                {cms.who_we_are_eyebrow}
              </p>
              <h2 className="font-heading text-3xl sm:text-4xl font-bold text-[#0A0A0A] mb-6">
                {cms.who_we_are_heading}
              </h2>
              {(cms.who_we_are_paragraphs || []).map((p, i) => (
                <p key={i} className="text-[#4B5563] leading-relaxed mb-4 last:mb-0">{p}</p>
              ))}
            </div>
            <div>
              <SectionMedia
                sectionKey="about-hero"
                className="w-full aspect-[4/3] rounded-sm"
              />
            </div>
          </div>
        </div>
      </section>

      {/* The Story Behind Novena */}
      <section className="py-16 bg-[#F8F9FA]" data-testid="our-story-section">
        <div className="px-6">
          <div className="max-w-2xl mx-auto">
            {/* Section Header */}
            <div className="text-center mb-12">
              <p className="text-sm uppercase tracking-[0.2em] text-[#BA1826] font-semibold mb-3">
                Our Story
              </p>
              <h2 className="font-heading text-2xl sm:text-3xl font-bold text-[#0A0A0A] mb-2">
                How It All Began
              </h2>
              <div className="w-16 h-[3px] bg-[#BA1826] mx-auto mt-4"></div>
            </div>

            {/* Story — continuous flow */}
            <div className="space-y-5 mb-10">
              <p className="text-[#4B5563] leading-relaxed text-sm">
                Novena Humanitarian Foundation was born from a vision that started with my brother, Jean-Eddy Silien.
              </p>
              <p className="text-[#4B5563] leading-relaxed text-sm">
                He created the clothing brand Novena with a purpose beyond fashion. His goal was to give back by supporting low income families locally and in Haiti. He had the hope of one day reaching communities around the world. People from every part of his life were drawn to his energy. Even without seeing the full vision, they believed in him. <span className="font-semibold italic text-[#0A0A0A]">Then everything changed.</span>
              </p>
              <p className="text-[#4B5563] leading-relaxed text-sm">
                We lost Jean-Eddy in a way that deeply shook our family and our community. His absence left a space that can never be filled. He was a son, a brother, a friend, and someone who brought people together in a way that is hard to describe.
              </p>
              <p className="text-[#4B5563] leading-relaxed text-sm">
                For years, I carried that loss quietly, trying to understand how to move forward and what would happen to the vision he started.
              </p>
              <p className="text-[#4B5563] leading-relaxed text-sm">
                Then one day, I realized the dream did not have to end. It could continue through me.
              </p>
              <p className="text-[#4B5563] leading-relaxed text-sm">
                After a powerful dream about my brother, everything became clear. I knew I had to carry this forward, not just as a brand, but as something greater. As I reflected on the memories and moments that were tied to him and our community, the purpose became undeniable. That is when the name came to me:
              </p>
              <p className="text-[#0A0A0A] leading-relaxed text-sm font-semibold">
                Novena Humanitarian Foundation.
              </p>
              <p className="text-[#4B5563] leading-relaxed text-sm">
                What started as an idea has grown into a foundation committed to supporting individuals and families facing hardship, both locally and abroad. We carry this work forward in his honor, focused on helping those in need and creating a path toward a stronger, more stable future.
              </p>
            </div>

            {/* Jean-Eddy Silien — at the bottom after the story */}
            <div className="mb-10 rounded-sm overflow-hidden shadow-lg" data-testid="story-image-1">
              <img
                src="https://customer-assets.emergentagent.com/job_humanitarian-aid-7/artifacts/u9aqc6cr_IMG_9954.jpeg"
                alt="Jean-Eddy Silien wearing a Novena shirt"
                className="w-full h-auto object-cover"
              />
              <div className="bg-white py-4 px-3 text-center">
                <p className="font-heading font-semibold text-[#0A0A0A] text-sm">Jean-Eddy Silien</p>
                <p className="text-xs text-[#4B5563] mt-1">&#x1F56F;&#xFE0F; In honor of his life and legacy</p>
              </div>
            </div>

            {/* CTA */}
            <div className="text-center pt-4">
              <Link to="/donate" className="btn-primary text-sm" data-testid="story-donate-btn">
                Continue the Mission
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="section-padding bg-[#F8F9FA]" data-testid="mission-vision">
        <div className="container-custom">
          <div className="grid md:grid-cols-2 gap-12">
            {/* Mission */}
            <div className="bg-white p-10 border border-[#E5E7EB]">
              <div className="w-14 h-14 bg-[#FEE2E2] rounded flex items-center justify-center mb-6">
                <Target className="w-7 h-7 text-[#BA1826]" />
              </div>
              <h3 className="font-heading text-2xl font-bold text-[#0A0A0A] mb-4">
                {cms.mission_heading}
              </h3>
              {(cms.mission_paragraphs || []).map((p, i) => (
                <p key={i} className={`text-[#4B5563] leading-relaxed ${i > 0 ? 'mt-4' : ''}`}>{p}</p>
              ))}
            </div>

            {/* Vision */}
            <div className="bg-white p-10 border border-[#E5E7EB]">
              <div className="w-14 h-14 bg-[#FEE2E2] rounded flex items-center justify-center mb-6">
                <Eye className="w-7 h-7 text-[#BA1826]" />
              </div>
              <h3 className="font-heading text-2xl font-bold text-[#0A0A0A] mb-4">
                {cms.vision_heading}
              </h3>
              {(cms.vision_paragraphs || []).map((p, i) => (
                <p key={i} className={`text-[#4B5563] leading-relaxed ${i > 0 ? 'mt-4' : ''}`}>{p}</p>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* What We Do */}
      <section className="section-padding bg-white" data-testid="what-we-do">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <p className="text-sm uppercase tracking-[0.15em] text-[#BA1826] font-semibold mb-4">
              What We Do
            </p>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-[#0A0A0A] mb-6">
              Essential Humanitarian Support
            </h2>
            <p className="text-[#4B5563] leading-relaxed">
              We are working to provide essential humanitarian support, including food assistance, hygiene 
              and toiletry supplies, emergency aid, and other daily necessities. All services 
              are delivered with a hands-on, dignity-first approach that prioritizes respect 
              and real impact.
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-6">
            {[
              { icon: '🍽️', title: 'Food Assistance', desc: 'Nutritious meals and groceries' },
              { icon: '🧴', title: 'Hygiene Supplies', desc: 'Toiletries and personal care' },
              { icon: '🩹', title: 'Emergency Aid', desc: 'Crisis support and relief' },
              { icon: '📦', title: 'Daily Necessities', desc: 'Essential everyday items' },
            ].map((item) => (
              <div key={item.title} className="text-center p-6">
                <div className="text-4xl mb-4">{item.icon}</div>
                <h4 className="font-heading font-semibold text-lg mb-2">{item.title}</h4>
                <p className="text-sm text-[#4B5563]">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why We Exist */}
      <section className="section-padding bg-[#0A0A0A]" data-testid="why-we-exist">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-sm uppercase tracking-[0.15em] text-[#BA1826] font-semibold mb-4">
                Why We Exist
              </p>
              <h2 className="font-heading text-3xl sm:text-4xl font-bold text-white mb-6">
                Because No One Should Have to Choose Between Dignity and Survival
              </h2>
              <p className="text-gray-400 leading-relaxed mb-6">
                Many organizations provide aid, but too often transparency comes at the cost 
                of privacy, or visibility outweighs respect. Novena was created to ensure that 
                donors clearly understand where support goes, while individuals and families 
                are helped without exploitation or loss of dignity.
              </p>
              <div className="bg-[#BA1826] p-6">
                <p className="font-heading text-xl text-white font-semibold italic">
                  &ldquo;Transparency without exploitation. Support without shame.&rdquo;
                </p>
              </div>
            </div>

            <div className="bg-white/5 p-8 border border-white/10">
              <h3 className="font-heading text-xl font-semibold text-white mb-6">
                How We Make an Impact
              </h3>
              <ul className="space-y-4">
                {howWeImpact.map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#BA1826] mt-0.5 flex-shrink-0" />
                    <span className="text-gray-300">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Where We Work */}
      <section className="section-padding bg-white" data-testid="where-we-work">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="order-2 lg:order-1">
              <SectionMedia
                sectionKey="about-team"
                className="w-full aspect-[4/3] rounded-sm"
              />
            </div>
            <div className="order-1 lg:order-2">
              <p className="text-sm uppercase tracking-[0.15em] text-[#BA1826] font-semibold mb-4">
                Where We Work
              </p>
              <h2 className="font-heading text-3xl sm:text-4xl font-bold text-[#0A0A0A] mb-6">
                Local Roots, Global Vision
              </h2>
              <p className="text-[#4B5563] leading-relaxed mb-4">
                Our work begins locally in the New York area, with a growing vision to 
                reach communities nationally and internationally.
              </p>
              <p className="text-[#4B5563] leading-relaxed mb-8">
                We aim to support people affected by economic hardship, instability, and 
                limited access to essential resources—wherever they may be.
              </p>
              <Link to="/projects" className="btn-primary" data-testid="view-projects-btn">
                View Our Projects
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-[#BA1826]" data-testid="about-cta">
        <div className="container-custom text-center">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-white mb-4">
            Join Us in Making a Difference
          </h2>
          <p className="text-white/80 max-w-xl mx-auto mb-8">
            Whether you donate, volunteer, or spread the word, your support helps us 
            continue serving communities with dignity and compassion.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link 
              to="/donate" 
              className="bg-white text-[#BA1826] px-8 py-3 font-semibold rounded hover:bg-gray-100 transition-all"
              data-testid="about-donate-btn"
            >
              Donate Now
            </Link>
            <Link 
              to="/involved" 
              className="bg-transparent text-white border-2 border-white px-8 py-3 font-semibold rounded hover:bg-white/10 transition-all"
              data-testid="about-involved-btn"
            >
              Get Involved
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
